import { DialogM } from "@common/components/alert/dialog/Dialog"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { groupAttributionProposalsService } from "@data/services/groupAttributionProposalsService"
import { buildPerSessionDisplayStrings, capitalize } from "@data/utility/Utils"
import { useNavigation, useRoute } from "@react-navigation/native"
import moment from "moment"
import React, { useEffect } from "react"
import { View } from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import FilledButtonM from "../../../common/components/button/FilledButtonM"
import TextM from "../../../common/components/text/TextM"
import { ChampionImageM } from "../../../student/champions/ChampionImageM"
import { SLOT_CHANGE_CHOOSE_IN_PROPOSALS, SLOT_CHANGE_KIND } from "../../proposals/slotChangeProposal"

function GroupAttributionProposalScreen(props) {
    const navigation = useNavigation()
    const route = useRoute()

    const proposalId = route?.params?.group_attribution_proposal_id ?? null
    const hash = route?.params?.hash ?? null

    const proposal = hash
        ? props.groupAttributionProposalByHash
        : props.groupAttributionProposals?.find(
              (p) => p.id === parseInt(proposalId)
          )

    const attribution = proposal?.group_attribution
    const liveSessions = attribution?.live_sessions || []

    const isPending = proposal?.status === "pending"

    // Format the time slots
    const formatTimeSlots = (sessions, label = "Créneaux") => {
        if (!sessions || !sessions.length) return ""

        // Group sessions by day
        const sessionsByDay = {}
        sessions.forEach((session) => {
            const dayTime = moment(
                session?.day_time,
                "dddd HH:mm",
                "en"
            ).locale("fr")
            const day = capitalize(dayTime.format("dddd"))
            const time = dayTime.format("HH[h]mm")

            if (!sessionsByDay[day]) {
                sessionsByDay[day] = []
            }

            sessionsByDay[day].push(
                `${time}-${moment(dayTime)
                    .add(session.duration, "minutes")
                    .format("HH[h]mm")}`
            )
        })

        // Format into string
        return (
            label +
            " : " +
            Object.entries(sessionsByDay)
                .map(([day, times]) => `${day} ${times.join(", ")}`)
                .join(", ")
        )
    }

    const getStartDateInfo = () => {
        if (!attribution?.start_date) return ""

        const date = moment(attribution.start_date)
        return `Première séance : ${date.format("dddd D MMMM [à] HH[h]mm")}`
    }

    // Build message based on whether it's adhoc or regular
    const isAdhoc = attribution?.is_adhoc
    const existingSessions = attribution?.existing_sessions || []
    const existingSessionIds = existingSessions.map((s) => s.id)
    const newSessions = liveSessions.filter(
        (s) => !existingSessionIds.includes(s.id)
    )

    let title = isPending ? "Nouvelle proposition de groupe" : "Dommage !"
    let studentInfo =
        proposal?.group_attribution?.school_subjects_classes_string
    let message = ""

    const perSessionMap =
        attribution?.school_subjects_classes_per_session
    const perSessionStrings = buildPerSessionDisplayStrings(liveSessions, perSessionMap)

    if (isPending && isAdhoc) {
        // Adhoc-specific display
        title = "Nouvelle proposition pour toi"

        // Get student details
        const adhocStudents = attribution?.adhoc_students || []
        if (adhocStudents.length > 0) {
            const studentNames = adhocStudents
                .map(
                    (s) =>
                        `${s.first_name} ${s.last_name}${
                            s.class ? ` (${s.class})` : ""
                        }`
                )
                .join(", ")
            studentInfo = `Nouvel élève : ${studentNames}`
        }

        const startDateInfo = getStartDateInfo()

        // Build sections for new and existing sessions
        const sections = [studentInfo]

        if (newSessions.length > 0) {
            sections.push(
                formatTimeSlots(newSessions, "Nouvelles séances à créer")
            )
        }

        if (existingSessions.length > 0) {
            sections.push(
                formatTimeSlots(
                    existingSessions,
                    "Séances existantes où l'élève sera ajouté"
                )
            )
        }

        if (startDateInfo) {
            sections.push(startDateInfo)
        }

        message = "\n" + sections.filter(Boolean).join("\n")

        // Add adhoc-specific message instead of comment
        message += "\n\nℹ️ Cette proposition est uniquement pour toi. Si tu ne l'acceptes pas, l'élève ne sera pas ajouté à tes séances existantes."
    } else if (isPending) {
        // Regular proposal display
        let sessionInfo = ""

        if (perSessionStrings.length > 0) {
            const studentMatch = studentInfo?.match(/^(\d+\s+élèves?)/)
            const studentCount = studentMatch ? studentMatch[1] : ""

            const sessionCount = perSessionStrings.length
            const sessionWord = sessionCount > 1 ? 'séances' : 'séance'
            studentInfo = studentCount ? `${studentCount} - ${sessionCount} ${sessionWord}` : ""

            sessionInfo = perSessionStrings.join("\n")
        } else {
            sessionInfo = formatTimeSlots(newSessions)
        }

        const startDateInfo = getStartDateInfo()
        message =
            "\n" +
            [studentInfo, sessionInfo, startDateInfo]
                .filter(Boolean)
                .join("\n\n")

        // Add comment only for regular proposals
        const comment = attribution?.comment
            ? `\nCommentaire : ${attribution.comment}`
            : ""
        message += comment
    }

    const description = isPending
        ? message
        : "Cette proposition n'est plus disponible."

    useEffect(() => {
        if (hash) {
            // Load proposal using hash
            props.apiRequest(
                groupAttributionProposalsService.getGroupAttributionProposalByHash,
                {},
                {},
                { hash }
            )
        } else {
            // Load proposal using ID and proposal data
            props.apiRequest(
                groupAttributionProposalsService.getGroupAttributionProposals
            )
        }
    }, [proposalId, hash])

    useApiStatus(
        hash
            ? groupAttributionProposalsService.updateGroupAttributionProposalStatusByHash
            : groupAttributionProposalsService.updateGroupAttributionProposalStatus,
        null,
        true,
        (successData) => {
            const status = successData?.status
            const message =
                status === "accepted"
                    ? "La proposition a été acceptée avec succès ! Tu recevras une confirmation si le groupe est validé pour toi."
                    : "La proposition a été masquée."

            setTimeout(() => {
                DialogM.show({
                    text1: "Succès",
                    text2: message,
                    buttonText1: "OK",
                    onPress: () => {},
                })
            }, 200)
            navigation.goBack()
            if (!hash) {
                props.apiRequest(
                    groupAttributionProposalsService.getGroupAttributionProposals
                )
            }
        }
    )

    const handleStatusUpdate = (status) => {
        if (hash) {
            // Use unguarded endpoint with hash
            props.apiRequest(
                groupAttributionProposalsService.updateGroupAttributionProposalStatusByHash,
                {},
                {
                    hash,
                    status,
                }
            )
        } else {
            // Use authenticated endpoint with ID
            props.apiRequest(
                groupAttributionProposalsService.updateGroupAttributionProposalStatus,
                {
                    id: proposalId,
                },
                {
                    status,
                }
            )
        }
    }

    const isLoading =
        props.getUserRequest || props.getGroupAttributionProposalByHashRequest
    const isUpdating =
        props.updateGroupAttributionProposalStatusRequest ||
        props.updateGroupAttributionProposalStatusByHashRequest

    return (
        <View
            style={{
                alignItems: "center",
                flex: 1,
                padding: 20,
            }}
        >
            {isLoading ? (
                <ActivityIndicator
                    color={theme.colors.primary}
                    style={{
                        marginTop: 10,
                    }}
                />
            ) : proposal ? (
                <>
                    <ChampionImageM
                        name="zoe"
                        variant={isPending ? "main" : "unhappy"}
                        width={140}
                    />
                    <TitleM
                        fontWeight="ExtraBold"
                        style={{
                            textAlign: "center",
                            marginTop: 10,
                            color: theme.colors.primary,
                        }}
                        wrap
                    >
                        {title}
                    </TitleM>
                    <TextM
                        fontWeight="Medium"
                        style={{ textAlign: "center" }}
                        wrap
                    >
                        {description}
                    </TextM>
                    {isPending && (
                        <>
                            <FilledButtonM
                                onPress={() => {
                                    // Un « Changement de créneau » s'accepte avec les créneaux choisis : ce choix vit dans « Propositions ».
                                    if (attribution?.kind === SLOT_CHANGE_KIND) {
                                        DialogM.show({
                                            text1: SLOT_CHANGE_CHOOSE_IN_PROPOSALS,
                                            buttonText1: "OK",
                                        })
                                        return
                                    }
                                    DialogM.show({
                                        text1: "Es-tu sûr.e de candidater à cette proposition ?",
                                        text2: `${description}\n\nEn candidatant, tu confirmes être prêt.e à assurer ces séances.\n\nTu recevras une confirmation si c'est validé pour toi.`,
                                        buttonText1: "Je candidate",
                                        onPress: () => {
                                            handleStatusUpdate("accepted")
                                        },
                                        buttonText2: "Annuler",
                                    })
                                }}
                                disabled={isUpdating}
                                color={theme.colors.primary}
                                style={{ marginTop: 20 }}
                                label={"Accepter la proposition"}
                            />
                            <FilledButtonM
                                onPress={() => {
                                    handleStatusUpdate("rejected")
                                }}
                                disabled={isUpdating}
                                color={theme.colors.error}
                                label={"Masquer la proposition"}
                            />
                        </>
                    )}
                </>
            ) : (
                <TextM fontWeight="Medium" style={{ textAlign: "center" }} wrap>
                    Cette demande n'est plus d'actualité
                </TextM>
            )}
            <FilledButtonM
                onPress={() => {
                    navigation.goBack()
                }}
                mode={"text"}
                label={"Retour"}
            />
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        groupAttributionProposals:
            state.data.currentUser?.object?.group_attribution_proposals || [],
        getUserRequest: state.api.getUser?.request,
        getGroupAttributionProposalByHashRequest:
            state.api.getGroupAttributionProposalByHash?.request,
        updateGroupAttributionProposalStatusRequest:
            state.api.updateGroupAttributionProposalStatus?.request,
        updateGroupAttributionProposalStatusByHashRequest:
            state.api.updateGroupAttributionProposalStatusByHash?.request,
        groupAttributionProposalByHash:
            state.data.formData?.groupAttributionProposalByHash,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(GroupAttributionProposalScreen)
